import React from 'react'
import moment from 'moment'
// @src imports
import { Button, Icon } from 'src/chrome'
import { dollarsAndCents } from 'src/helpers'
import { PlanFragment, StripeSubscription } from 'src/graphql'
import { Plan, PlanDetails } from 'src/marketing/plans/types'

import { colors, style } from './pricingTableStyles'

type PlanTileProps = {
  plan: Plan | PlanFragment
  details?: PlanDetails
  containerStyle?: React.CSSProperties
  onSelect: (plan: string) => void
  isCurrentPlan: boolean
  hideButton?: boolean
  stripeSubscription?: Partial<StripeSubscription>
  getPlan: (planId: string) => PlanFragment | undefined
}

// tslint:disable-next-line: cyclomatic-complexity score 27
const PlanTile: React.FC<PlanTileProps> = props => {
  const {
    plan,
    details,
    onSelect,
    isCurrentPlan,
    containerStyle,
    hideButton = false,
    stripeSubscription,
    getPlan,
  } = props
  const amount = dollarsAndCents(plan.price.amount ?? 0)
  const primary: string =
    !isCurrentPlan && plan.color
      ? (colors.primary as any)[plan.color]
      : colors.white
  const secondary: string =
    !isCurrentPlan && plan.color
      ? (colors.secondary as any)[plan.color]
      : colors.white
  const tertiary: string =
    !isCurrentPlan && plan.color
      ? (colors.tertiary as any)[plan.color]
      : colors.white

  const tileBackground: string = isCurrentPlan ? colors.active : colors.white
  const planOnCancel = stripeSubscription?.metadata?.planOnCancel
  const planOnCancelTitle = planOnCancel ? getPlan(planOnCancel)?.title : null
  return (
    <div
      style={{
        background: tileBackground,
        ...style.planHeader,
        ...containerStyle,
      }}
    >
      {isCurrentPlan && (
        <div style={style.chip}>
          <p style={style.chipLabel}>Current Plan</p>
        </div>
      )}
      <div
        style={{
          ...style.titleBlock,
          borderBottom: `.5px solid ${secondary}`,
        }}
      >
        <h2 style={{ ...style.planTitle, color: primary }}>
          {plan.title.toUpperCase()}
        </h2>
      </div>
      <div style={style.amountBlock}>
        <div style={{ display: 'flex' }}>
          <div style={{ marginTop: 10 }}>
            <Icon icon={'DOLLARSIGN'} size={18} color={secondary} />
          </div>
          <span style={{ ...style.dollarAmount, color: secondary }}>
            {amount.dollars}
          </span>
          <span
            style={{
              ...style.centsAmount,
              color: secondary,
            }}
          >{`.${amount.cents}`}</span>
        </div>
        <div style={{ color: tertiary }}>/ month</div>
        {details && (
          <ul style={{ marginTop: 15, fontSize: 14 }}>
            {details.details &&
              details.details.map(detail => (
                <li
                  key={detail.detail}
                  style={{
                    ...{ listStyleType: 'none', padding: '3px 0' },
                    ...(isCurrentPlan
                      ? {
                          listStyleType: 'none',
                          color: primary,
                          fontWeight: 600,
                        }
                      : { listStyleType: 'none', color: 'rgba(0,0,0,.55)' }),
                  }}
                >
                  {detail.innerDetails && (
                    <>
                      <div style={{ fontWeight: 'bold' }}>{detail.detail}</div>
                      <div style={{ fontWeight: 'bold' }}>
                        (postage not included)
                      </div>
                      <ul>
                        {detail.innerDetails.map(innerDetail => (
                          <li
                            key={innerDetail.detail}
                            style={{
                              ...{
                                listStyleType: 'none',
                                padding: '3px 0',
                                paddingLeft: '12px',
                              },
                              ...(isCurrentPlan
                                ? {
                                    listStyleType: 'none',
                                    color: primary,
                                    fontWeight: 600,
                                  }
                                : {
                                    listStyleType: 'none',
                                    color: 'rgba(0,0,0,.55)',
                                  }),
                            }}
                          >
                            {innerDetail.detail}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                  {!detail.innerDetails && detail.detail}
                </li>
              ))}
          </ul>
        )}
        {!hideButton && (
          <Button
            id={`add_${plan.title.toLowerCase()}_btn`}
            title={isCurrentPlan ? 'Add System Package' : 'Select'}
            onClick={() => onSelect(plan.title)}
            style={{
              ...style.selectButton,
            }}
          />
        )}
        {isCurrentPlan &&
          plan.title !== 'Basic' &&
          stripeSubscription &&
          !stripeSubscription.setToCancel && (
            <div
              style={{
                height: '30px',
                position: 'absolute',
                bottom: '0px',
                right: '15px',
                cursor: 'pointer',
              }}
              onClick={() => onSelect('Basic')}
            >
              <p style={{ color: '#fff', textAlign: 'center', fontSize: 18 }}>
                Cancel
              </p>
            </div>
          )}
        {isCurrentPlan &&
          plan.title !== 'Basic' &&
          stripeSubscription &&
          stripeSubscription.setToCancel && (
            <div
              style={{
                height: '30px',
                position: 'absolute',
                bottom: '0px',
              }}
            >
              <p
                style={{
                  color: '#fff',
                  textAlign: 'center',
                  fontSize: 15,
                  marginTop: '3px',
                }}
              >
                {planOnCancelTitle
                  ? `Your ${planOnCancelTitle} plan will start`
                  : `Will cancel on`}{' '}
                {moment(stripeSubscription.currentPeriodEnd).format('M/D/YYYY')}
              </p>
            </div>
          )}
      </div>
    </div>
  )
}

export default PlanTile
